import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '@/hooks';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ProcedureTemplate } from '@/types/procedure';
import type { ReportListItem } from '@/types/report';
import { ReportCard } from './ReportCard';
import { ReportCardSkeleton } from './ReportCardSkeleton';
import { ReportsEmptyState } from './ReportsEmptyState';
import { CARD_GRID_COLUMNS_CLASSES } from '@/constants/layout';
import { cn } from '@/lib/utils';

interface ReportGridResultsProps {
    reports: ReportListItem[];
    isReportSelected: (report: ReportListItem) => boolean;
    isMultiSelectMode: boolean;
    onReportClick: (
        reportId: string,
        event?: MouseEvent<HTMLDivElement>,
    ) => void;
    onDeleteReport: (reportId: string) => void;
    onEditReport: (report: ReportListItem) => void;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    hasMore: boolean;
    isInitialLoading: boolean;
    scrollContainer: HTMLDivElement | null;
    hasActiveQuery: boolean;
    procedureTemplates: ProcedureTemplate[];
    onCreateReport: (template?: ProcedureTemplate) => void;
}

export function ReportGridResults({
    reports,
    isReportSelected,
    isMultiSelectMode,
    onReportClick,
    onDeleteReport,
    onEditReport,
    onLoadMore,
    isLoadingMore,
    hasMore,
    isInitialLoading,
    scrollContainer,
    hasActiveQuery,
    procedureTemplates,
    onCreateReport,
}: ReportGridResultsProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    useInfiniteScroll(scrollContainer, {
        onLoadMore,
        isLoading: isLoadingMore,
        hasMore,
    });

    if (isInitialLoading) {
        return (
            <div className={cn('grid gap-4 py-4', CARD_GRID_COLUMNS_CLASSES)}>
                {Array.from({ length: 9 }).map((_, index) => (
                    <ReportCardSkeleton key={index} />
                ))}
            </div>
        );
    }

    if (reports.length === 0 && !isLoadingMore) {
        if (hasActiveQuery) {
            return (
                <div className="flex items-center justify-center py-20">
                    <p className="text-grey-300">{t('noReportsFound')}</p>
                </div>
            );
        }
        return (
            <ReportsEmptyState
                procedureTemplates={procedureTemplates}
                onCreateReport={onCreateReport}
            />
        );
    }

    return (
        <>
            <div className={cn('grid gap-4 py-4', CARD_GRID_COLUMNS_CLASSES)}>
                {reports.map((report) => (
                    <ReportCard
                        key={report.id}
                        report={report}
                        isMultiSelectMode={isMultiSelectMode}
                        checked={
                            isMultiSelectMode
                                ? isReportSelected(report)
                                : undefined
                        }
                        onClick={onReportClick}
                        onDelete={onDeleteReport}
                        onEdit={() => onEditReport(report)}
                    />
                ))}
            </div>

            {isLoadingMore ? (
                <div className="flex justify-center py-4">
                    <p className="text-grey-300 text-sm">{t('loadingMore')}</p>
                </div>
            ) : null}
            {!hasMore && reports.length > 0 ? (
                <div className="flex justify-center py-4">
                    <p className="text-grey-400 text-sm">
                        {t('allReportsLoaded')}
                    </p>
                </div>
            ) : null}
        </>
    );
}
